import { findMatchesInSelector, injectClientHighlightSpans } from "./helpers";

export const TspRules = {
  name: "Tech Science Press",
  rules: [
    {
      id: "tspTitleCase",
      name: "Title Case Style",
      description: "Enforces Title Case (ULC except prepositions and conjunctions) for the main article title.",
      doc: "### Title Case Style\nTitles must be formatted in Title Case (ULC). Capitalize the first letter of all words except prepositions (e.g., in, on, at, by, for, with, about, to) and conjunctions (e.g., and, but, or, yet, so, nor).",
      category: "Front Matter",
      execute(editor) {
        const matches = [];
        const doc = editor.getDoc();
        const titles = doc.querySelectorAll("title1, article-title, h1.title");
        const lowerWords = new Set(["a", "an", "the", "and", "but", "or", "for", "nor", "on", "in", "at", "by", "to", "with", "about", "between", "from", "of", "up", "down", "as", "into"]);

        titles.forEach(title => {
          const walker = doc.createTreeWalker(title, NodeFilter.SHOW_TEXT, null, false);
          let node;
          while ((node = walker.nextNode())) {
            const val = node.nodeValue;
            const words = val.split(/(\s+)/);
            let changed = false;
            const updatedWords = words.map((w, index) => {
              if (/^\s*$/.test(w)) return w;
              const cleanWord = w.replace(/^[^\w\s]+|[^\w\s]+$/g, "");
              if (!cleanWord) return w;
              
              const isFirst = index === 0;
              const isLower = lowerWords.has(cleanWord.toLowerCase());
              
              const expectedWord = (isFirst || !isLower) 
                ? cleanWord.charAt(0).toUpperCase() + cleanWord.slice(1).toLowerCase()
                : cleanWord.toLowerCase();
              
              const prefix = w.slice(0, w.indexOf(cleanWord));
              const suffix = w.slice(w.indexOf(cleanWord) + cleanWord.length);
              const reconstructed = prefix + expectedWord + suffix;
              
              if (w !== reconstructed) {
                changed = true;
                return reconstructed;
              }
              return w;
            });

            if (changed) {
              const suggested = updatedWords.join("");
              const uid = `tspTitleCase-${Math.random().toString(36).slice(2, 9)}`;
              matches.push({
                text: val,
                suggested,
                node,
                start: 0,
                end: val.length,
                processed: false,
                name: "Title Case Style",
                styleId: "tspTitleCase",
                uid
              });
            }
          }
        });

        // Also check terminal period in title
        titles.forEach(title => {
          const text = title.textContent || "";
          if (text.trim().endsWith(".") && !text.trim().endsWith("...")) {
            const cleaned = text.trim().slice(0, -1);
            const walker = doc.createTreeWalker(title, NodeFilter.SHOW_TEXT, null, false);
            let node = walker.nextNode();
            if (node) {
              const val = node.nodeValue;
              const lastDotIdx = val.lastIndexOf(".");
              if (lastDotIdx !== -1) {
                const uid = `tspTitlePeriod-${Math.random().toString(36).slice(2, 9)}`;
                matches.push({
                  text: val.slice(lastDotIdx),
                  suggested: "",
                  node,
                  start: lastDotIdx,
                  end: val.length,
                  processed: false,
                  name: "Remove Trailing Period from Title",
                  styleId: "tspTitlePeriod",
                  uid
                });
              }
            }
          }
        });

        injectClientHighlightSpans(editor, matches, "highlight-title");
        return matches;
      }
    },
    {
      id: "tspAuthorGroup",
      name: "Author Group Format",
      description: "Checks author names for space between initials, correct separators ('and' or comma), and removes academic honorifics/designations.",
      doc: "### Author Group Formatting\nEnforces correct separators ('and' for two authors, commas and 'and' for more than two). Spacing between initials is required, and professional designations (Dr., Prof., Mr., Ms.) must be removed.",
      category: "Front Matter",
      execute(editor) {
        const matches = [];
        const initPattern = /\b([A-Z]\.)([A-Z]\.)\b/g;
        const initMatches = findMatchesInSelector(editor, "contrib, .author-name", "tspAuthorGroupInitials", initPattern, "Author Initials Spacing", (m) => `${m[1]} ${m[2]}`);
        matches.push(...initMatches);

        const honorificPattern = /\b(Dr\.|Prof\.|Mrs\.|Miss\.|Mr\.|Ms\.)\s+/gi;
        const honorificMatches = findMatchesInSelector(editor, "contrib, .author-name", "tspAuthorGroupHonorifics", honorificPattern, "Remove Author Honorifics", () => "");
        matches.push(...honorificMatches);

        injectClientHighlightSpans(editor, matches, "highlight-author");
        return matches;
      }
    },
    {
      id: "tspAffiliations",
      name: "Affiliation Formatting",
      description: "Corrects abbreviations like 'Dept.' to 'Department', changes 'People's Republic of China' to 'China', removes job titles/designations, and removes trailing periods.",
      doc: "### Affiliation Guidelines\nAffiliations should not end with a period. Change 'People's Republic of China' to 'China' and 'Dept.' to 'Department'. Personal designations/job titles (e.g., Associate Professor, Lecturer) must be stripped.",
      category: "Front Matter",
      execute(editor) {
        const matches = [];
        const doc = editor.getDoc();
        
        const deptPattern = /\bDept\./gi;
        const deptMatches = findMatchesInSelector(editor, "aff, .affiliation-line", "tspAffiliationDept", deptPattern, "Change Dept. to Department", () => "Department");
        matches.push(...deptMatches);

        const prcPattern = /\bPeople's\s+Republic\s+of\s+China\b/gi;
        const prcMatches = findMatchesInSelector(editor, "aff, .affiliation-line", "tspAffiliationChina", prcPattern, "People's Republic of China to China", () => "China");
        matches.push(...prcMatches);

        const desPattern = /\b(Assistant\s+Professor|Associate\s+Professor|Professor|Research\s+Scholar|PhD\s+Student|PhD|Lecturer)\b,?\s*/gi;
        const desMatches = findMatchesInSelector(editor, "aff, .affiliation-line", "tspAffiliationDesignation", desPattern, "Remove Job Title", () => "");
        matches.push(...desMatches);

        const affs = doc.querySelectorAll("aff, .affiliation-line");
        affs.forEach(aff => {
          const walker = doc.createTreeWalker(aff, NodeFilter.SHOW_TEXT, null, false);
          let lastNode = null;
          let node;
          while ((node = walker.nextNode())) {
            lastNode = node;
          }
          if (lastNode && lastNode.nodeValue.trim().endsWith(".") && !lastNode.nodeValue.trim().endsWith("...")) {
            const val = lastNode.nodeValue;
            const lastDotIdx = val.lastIndexOf(".");
            const uid = `tspAffiliationPeriod-${Math.random().toString(36).slice(2, 9)}`;
            matches.push({
              text: val.substring(lastDotIdx),
              suggested: val.substring(lastDotIdx).replace(/\.$/, ""),
              node: lastNode,
              start: lastDotIdx,
              end: val.length,
              processed: false,
              name: "Remove Affiliation End Period",
              styleId: "tspAffiliationPeriod",
              uid
            });
          }
        });

        injectClientHighlightSpans(editor, matches, "highlight-affiliation");
        return matches;
      }
    },
    {
      id: "tspCorrespondingAuthor",
      name: "Corresponding Author Formatting",
      description: "Ensures correct prefix (*Corresponding Author: or *Corresponding Authors:) and removes trailing period from equal contribution statements.",
      doc: "### Corresponding Author Guidelines\nUse single/plural prefixes depending on the author count. Ensure there is no ending period for equal contribution statements.",
      category: "Front Matter",
      execute(editor) {
        const matches = [];
        const doc = editor.getDoc();
        
        const corresps = doc.querySelectorAll("corresp, .corresponding-author");
        corresps.forEach(corresp => {
          const text = corresp.textContent || "";
          const emailCount = (text.match(/@/g) || []).length;
          
          const walker = doc.createTreeWalker(corresp, NodeFilter.SHOW_TEXT, null, false);
          let node;
          while ((node = walker.nextNode())) {
            const val = node.nodeValue;
            
            if (emailCount === 1 && /\*Corresponding\s+Authors:/i.test(val)) {
              const uid = `tspCorrespPrefix-${Math.random().toString(36).slice(2, 9)}`;
              const idx = val.search(/\*Corresponding\s+Authors:/i);
              matches.push({
                text: val.slice(idx, idx + "*Corresponding Authors:".length),
                suggested: "*Corresponding Author:",
                node,
                start: idx,
                end: idx + "*Corresponding Authors:".length,
                processed: false,
                name: "Use Singular 'Corresponding Author'",
                styleId: "tspCorrespPrefix",
                uid
              });
            } else if (emailCount > 1 && /\*Corresponding\s+Author:/i.test(val)) {
              const uid = `tspCorrespPrefix-${Math.random().toString(36).slice(2, 9)}`;
              const idx = val.search(/\*Corresponding\s+Author:/i);
              matches.push({
                text: val.slice(idx, idx + "*Corresponding Author:".length),
                suggested: "*Corresponding Authors:",
                node,
                start: idx,
                end: idx + "*Corresponding Author:".length,
                processed: false,
                name: "Use Plural 'Corresponding Authors'",
                styleId: "tspCorrespPrefix",
                uid
              });
            }

            const equalContribPattern = /(these\s+authors\s+contributed\s+equally\s+to\s+this\s+work)\./gi;
            let m;
            while ((m = equalContribPattern.exec(val)) !== null) {
              const uid = `tspEqualContrib-${Math.random().toString(36).slice(2, 9)}`;
              matches.push({
                text: m[0],
                suggested: m[1],
                node,
                start: m.index,
                end: m.index + m[0].length,
                processed: false,
                name: "Remove Trailing Period from Contribution Statement",
                styleId: "tspEqualContrib",
                uid
              });
            }
          }
        });

        const emails = doc.querySelectorAll("corresp email, mail1, .corresp-email");
        emails.forEach(email => {
          const text = email.textContent || "";
          let corrected = text.replace(/\s+/g, "");
          if (corrected.endsWith(".")) {
            corrected = corrected.slice(0, -1);
          }
          if (text !== corrected) {
            const walker = doc.createTreeWalker(email, NodeFilter.SHOW_TEXT, null, false);
            let node = walker.nextNode();
            if (node) {
              const uid = `tspCorrespondingEmail-${Math.random().toString(36).slice(2, 9)}`;
              matches.push({
                text: node.nodeValue,
                suggested: corrected,
                node,
                start: 0,
                end: node.nodeValue.length,
                processed: false,
                name: "Remove spaces and trailing periods from email",
                styleId: "tspCorrespondingEmail",
                uid
              });
            }
          }
        });

        injectClientHighlightSpans(editor, matches, "highlight-corresponding");
        return matches;
      }
    },
    {
      id: "tspHistoryDates",
      name: "History Date Formatting",
      description: "Ensures day of month is zero-padded to two digits, and history lines do not end with periods.",
      doc: "### History Dates\nDates in the history block (Received, Accepted, Published) must use two digits for the day of the month (e.g., 'Received: 09 November 2019' instead of 'Received: 9 November 2019'). History lines must not end with a period.",
      category: "Front Matter",
      execute(editor) {
        const matches = [];
        const doc = editor.getDoc();
        const historyLines = doc.querySelectorAll("history, .history-line");

        historyLines.forEach(hist => {
          const walker = doc.createTreeWalker(hist, NodeFilter.SHOW_TEXT, null, false);
          let lastNode = null;
          let node;
          while ((node = walker.nextNode())) {
            lastNode = node;
          }
          if (lastNode && lastNode.nodeValue.trim().endsWith(".") && !lastNode.nodeValue.trim().endsWith("...")) {
            const val = lastNode.nodeValue;
            const idx = val.lastIndexOf(".");
            const uid = `tspHistoryPeriod-${Math.random().toString(36).slice(2, 9)}`;
            matches.push({
              text: val.substring(idx),
              suggested: val.substring(idx).replace(/\.$/, ""),
              node: lastNode,
              start: idx,
              end: val.length,
              processed: false,
              name: "Remove History Line Period",
              styleId: "tspHistoryPeriod",
              uid
            });
          }
        });

        const datePattern = /\b(Received|Accepted|Published)\s*:\s*(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})/gi;
        const dateMatches = findMatchesInSelector(editor, "history, .history-line", "tspHistoryDates", datePattern, "History Date Formatting", (m) => {
          const type = m[1];
          const day = m[2].padStart(2, "0");
          const month = m[3];
          const year = m[4];
          return `${type}: ${day} ${month} ${year}`;
        });

        matches.push(...dateMatches);
        injectClientHighlightSpans(editor, matches, "highlight-history");
        return matches;
      }
    },
    {
      id: "tspAbstractCitations",
      name: "Remove Abstract Citations",
      description: "Removes bracketed or numbered reference citations from the abstract.",
      doc: "### No Citations in Abstract\nAbstracts must summarize the paper without referring to external reference keys (e.g. '[1]', '[2, 5]'). This rule flags and strips citations inside the abstract element.",
      category: "Abstract",
      execute(editor) {
        const pattern = /\s*\[\d+(,\s*\d+)*(-\d+)?\]/g;
        const matches = findMatchesInSelector(editor, "abstract, .abstract-text", "tspAbstractCitations", pattern, "Remove Abstract Citations", () => "");
        injectClientHighlightSpans(editor, matches, "highlight-abstract-citation");
        return matches;
      }
    },
    {
      id: "tspKeywordFormatting",
      name: "Keyword Formatting",
      description: "Ensures keyword list does not end in a period and only the very first word of the first keyword is capitalized (except proper nouns).",
      doc: "### Keyword Formatting\nNo terminal periods on keywords. Only the first letter of the first keyword should be uppercase; all other keywords must be lowercase (unless they are proper nouns or acronyms).",
      category: "Keywords",
      execute(editor) {
        const matches = [];
        const doc = editor.getDoc();
        const kwds = doc.querySelectorAll("kwd-group, .keywords-line");

        kwds.forEach(group => {
          const walker = doc.createTreeWalker(group, NodeFilter.SHOW_TEXT, null, false);
          let lastNode = null;
          let node;
          while ((node = walker.nextNode())) {
            lastNode = node;
          }
          if (lastNode && lastNode.nodeValue.trim().endsWith(".") && !lastNode.nodeValue.trim().endsWith("...")) {
            const val = lastNode.nodeValue;
            const idx = val.lastIndexOf(".");
            const uid = `tspKeywordPeriod-${Math.random().toString(36).slice(2, 9)}`;
            matches.push({
              text: val.substring(idx),
              suggested: val.substring(idx).replace(/\.$/, ""),
              node: lastNode,
              start: idx,
              end: val.length,
              processed: false,
              name: "Remove Keyword Terminal Period",
              styleId: "tspKeywordPeriod",
              uid
            });
          }
          
          const firstNode = doc.createTreeWalker(group, NodeFilter.SHOW_TEXT, null, false).nextNode();
          if (firstNode) {
            const val = firstNode.nodeValue;
            const prefixMatch = val.match(/^(Keywords:\s*)([A-Za-z])/i);
            if (prefixMatch) {
              const prefixLen = prefixMatch[1].length;
              const firstChar = prefixMatch[2];
              if (firstChar !== firstChar.toUpperCase()) {
                const uid = `tspKeywordCase-${Math.random().toString(36).slice(2, 9)}`;
                matches.push({
                  text: firstChar,
                  suggested: firstChar.toUpperCase(),
                  node: firstNode,
                  start: prefixLen,
                  end: prefixLen + 1,
                  processed: false,
                  name: "Capitalize first keyword",
                  styleId: "tspKeywordCase",
                  uid
                });
              }
            }

            const kwPart = val.substring(val.indexOf(":") + 1);
            const items = kwPart.split(/([,;]\s*)/);
            let updated = false;
            const newItems = items.map((item, idx) => {
              if (idx % 2 === 0 && idx > 0) {
                const trimmed = item.trim();
                if (trimmed && /^[A-Z][a-z]/.test(trimmed)) {
                  updated = true;
                  const firstL = trimmed.charAt(0).toLowerCase();
                  const rest = trimmed.slice(1);
                  const spacePrefix = item.length - trimmed.length;
                  return " ".repeat(spacePrefix) + firstL + rest;
                }
              }
              return item;
            });
            if (updated) {
              const uid = `tspKeywordSubCase-${Math.random().toString(36).slice(2, 9)}`;
              matches.push({
                text: kwPart,
                suggested: newItems.join(""),
                node: firstNode,
                start: val.indexOf(":") + 1,
                end: val.length,
                processed: false,
                name: "Lowercase subsequent keywords",
                styleId: "tspKeywordSubCase",
                uid
              });
            }
          }
        });

        injectClientHighlightSpans(editor, matches, "highlight-keywords");
        return matches;
      }
    },
    {
      id: "tspAbbreviations",
      name: "Abbreviations Case and Layout",
      description: "Enforces sentence case for Nomenclature/Abbreviation headings and checks that double column format is avoided.",
      doc: "### Abbreviations & Nomenclature\nHeading titles (Nomenclature, Abbreviations, Greek Symbols) should be in sentence case (e.g. 'Greek symbols' rather than 'GREEK SYMBOLS'). These sections must not use a double column layout.",
      category: "Abbreviations",
      execute(editor) {
        const matches = [];
        const doc = editor.getDoc();
        
        const headings = doc.querySelectorAll("h1, h2, h3, title1, sec-title");
        headings.forEach(heading => {
          const text = heading.textContent || "";
          if (/^(Abbreviations|Nomenclature|Greek\s+Symbols)$/i.test(text.trim())) {
            const trimmed = text.trim();
            const expected = trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
            if (trimmed !== expected) {
              const walker = doc.createTreeWalker(heading, NodeFilter.SHOW_TEXT, null, false);
              const node = walker.nextNode();
              if (node) {
                const uid = `tspAbbrHeading-${Math.random().toString(36).slice(2, 9)}`;
                matches.push({
                  text: node.nodeValue,
                  suggested: expected,
                  node,
                  start: 0,
                  end: node.nodeValue.length,
                  processed: false,
                  name: "Sentence Case Heading",
                  styleId: "tspAbbrHeading",
                  uid
                });
              }
            }
          }
        });

        injectClientHighlightSpans(editor, matches, "highlight-abbreviations");
        return matches;
      }
    },
    {
      id: "tspHeadings",
      name: "Heading Level Style",
      description: "Verifies that heading levels 1-3 are numbered and level 4 is unnumbered, adhering to heading hierarchy.",
      doc: "### Heading Styles\n- Heading Level 1: Numbered, bold, roman (e.g. 1 Introduction)\n- Heading Level 2: Numbered, bold, italic (e.g. 2.1 Materials and Methods)\n- Heading Level 3: Numbered, unbold, italic (e.g. 2.1.1 Conventional Synthesis)\n- Heading Level 4: Unnumbered, unbold, italic (e.g. FTIR Spectroscopy)\nHeading levels 4 or higher must not have numbering.",
      category: "Body Text",
      execute(editor) {
        const matches = [];
        const doc = editor.getDoc();
        
        const h4s = doc.querySelectorAll("h4, .heading4, .level4-heading");
        h4s.forEach(h4 => {
          const text = h4.textContent || "";
          const numPattern = /^(\d+(\.\d+)+)\s+/;
          const match = text.match(numPattern);
          if (match) {
            const walker = doc.createTreeWalker(h4, NodeFilter.SHOW_TEXT, null, false);
            const node = walker.nextNode();
            if (node) {
              const uid = `tspHeading4NoNum-${Math.random().toString(36).slice(2, 9)}`;
              matches.push({
                text: match[0],
                suggested: "",
                node,
                start: 0,
                end: match[0].length,
                processed: false,
                name: "Unnumber Heading Level 4",
                styleId: "tspHeading4NoNum",
                uid
              });
            }
          }
        });

        injectClientHighlightSpans(editor, matches, "highlight-headings");
        return matches;
      }
    },
    {
      id: "tspFiguresTables",
      name: "Figures & Tables Citations and Captions",
      description: "Enforces abbreviated figure citation styles (Fig. / Figs.) and sentence case captions for figures and tables without ending periods.",
      doc: "### Figures & Tables Formatting\n- Citation: Figures should be cited as 'Fig. 1', 'Figs. 3–5', or 'Figs. 7a and 7b'.\n- Captions: Figure captions ('Figure 1: ...') and Table captions ('Table 1: ...') should be in sentence case and must not end with a period.",
      category: "Figures and Tables",
      execute(editor) {
        const matches = [];
        const doc = editor.getDoc();

        const figCitationPattern = /\bFigure\s+(\d+)\b/g;
        const figCitationMatches = findMatchesInSelector(editor, "body1, p, abstract", "tspFigCitation", figCitationPattern, "Use 'Fig.' abbreviation", (m) => `Fig. ${m[1]}`);
        matches.push(...figCitationMatches);

        const figsCitationPattern = /\bFigures\s+(\d+)\b/g;
        const figsCitationMatches = findMatchesInSelector(editor, "body1, p, abstract", "tspFigsCitation", figsCitationPattern, "Use 'Figs.' abbreviation", (m) => `Figs. ${m[1]}`);
        matches.push(...figsCitationMatches);

        const captions = doc.querySelectorAll("figcaption, fig caption, table caption, .fig-caption, .table-caption");
        captions.forEach(caption => {
          const text = caption.textContent || "";
          if (text.trim().endsWith(".") && !text.trim().endsWith("...")) {
            const walker = doc.createTreeWalker(caption, NodeFilter.SHOW_TEXT, null, false);
            let lastNode = null;
            let node;
            while ((node = walker.nextNode())) {
              lastNode = node;
            }
            if (lastNode) {
              const val = lastNode.nodeValue;
              const idx = val.lastIndexOf(".");
              const uid = `tspCaptionPeriod-${Math.random().toString(36).slice(2, 9)}`;
              matches.push({
                text: val.substring(idx),
                suggested: val.substring(idx).replace(/\.$/, ""),
                node: lastNode,
                start: idx,
                end: val.length,
                processed: false,
                name: "Remove Caption End Period",
                styleId: "tspCaptionPeriod",
                uid
              });
            }
          }
        });

        injectClientHighlightSpans(editor, matches, "highlight-fig-table");
        return matches;
      }
    },
    {
      id: "tspEquations",
      name: "Equation Citations",
      description: "Ensures equations in text are cited as 'Eq. (X)' or 'Eqs. (X)–(Y)' and display equations are numbered consecutively in parentheses.",
      doc: "### Equation Citations\nIn running text, equations must be cited using 'Eq. (1)' or 'Eqs. (28)–(30)'. Displays must use consecutive numbers in parentheses flush right, while equation is flush left.",
      category: "Equations",
      execute(editor) {
        const matches = [];
        
        const eqCitationPattern = /\bEquation\s+(\d+)\b/g;
        const eqCitationMatches = findMatchesInSelector(editor, "body1, p, abstract", "tspEqCitation", eqCitationPattern, "Use 'Eq. (X)' format", (m) => `Eq. (${m[1]})`);
        matches.push(...eqCitationMatches);

        const eqCitationPattern2 = /\bEq\.\s+(\d+)\b/g;
        const eqCitationMatches2 = findMatchesInSelector(editor, "body1, p, abstract", "tspEqCitationParens", eqCitationPattern2, "Add parentheses to Eq. citation", (m) => `Eq. (${m[1]})`);
        matches.push(...eqCitationMatches2);

        injectClientHighlightSpans(editor, matches, "highlight-equations");
        return matches;
      }
    },
    {
      id: "tspReferenceCitations",
      name: "Reference Citation Format",
      description: "Removes 'Ref.'/'Refs.' prefix before citations, checks for et al. usage with author surnames, and ensures square brackets and en-dashes are used.",
      doc: "### Reference Citations\n- Do not use 'Ref.' or 'Refs.' before citations (e.g., 'in [5]' instead of 'in Ref. [5]').\n- For one author: 'Surname [1]'. For two or more authors: 'Surname et al. [2]'.\n- Use en-dashes for ranges (e.g., [4–6] instead of [4-6]).",
      category: "References",
      execute(editor) {
        const matches = [];
        
        const refPattern = /\bRefs?\.\s+(\[\d+\])/g;
        const refMatches = findMatchesInSelector(editor, "body1, p, abstract", "tspRemoveRefPrefix", refPattern, "Remove Ref./Refs. prefix", (m) => m[1]);
        matches.push(...refMatches);

        const rangePattern = /\[(\d+)-(\d+)\]/g;
        const rangeMatches = findMatchesInSelector(editor, "body1, p, abstract", "tspCitationRangeDash", rangePattern, "Use En-dash in citation range", (m) => `[${m[1]}–${m[2]}]`);
        matches.push(...rangeMatches);

        injectClientHighlightSpans(editor, matches, "highlight-citation-style");
        return matches;
      }
    },
    {
      id: "tspDashes",
      name: "En-dash and Em-dash Spacing",
      description: "Ensures no spaces around En-dashes (ranges) and Em-dashes (parentheticals).",
      doc: "### Dashes spacing\n- En-dashes (–) and Em-dashes (—) should not have spaces before or after them (e.g. '10–15 nm' instead of '10 – 15 nm').",
      category: "Body Text",
      execute(editor) {
        const matches = [];
        
        const dashPattern = /\s*([–—])\s*/g;
        const dashMatches = findMatchesInSelector(editor, "body1, p, abstract", "tspDashSpacing", dashPattern, "Remove spaces around dash", (m) => m[1]);
        matches.push(...dashMatches);

        injectClientHighlightSpans(editor, matches, "highlight-dashes");
        return matches;
      }
    },
    {
      id: "tspSpecificStylePoints",
      name: "Specific Style Points",
      description: "Enforces punctuation of 'i.e.,' and 'e.g.,', italicizes 'p' in p-value, adds space between numbers and units, ensures correct abbreviation for time units (h, min, s), and italicizes foreign expressions.",
      doc: "### Specific Style Guidelines\n- Add a comma after i.e. and e.g. (to get 'i.e.,' and 'e.g.,').\n- Change 'Ltd' to 'Ltd.'.\n- Italicize the 'p' in p-value.\n- Add space between number and unit (e.g. '2 cm' instead of '2cm').\n- Convert hours, minutes, seconds (sec) to h, min, s.\n- Italicize 'versus' as 'vs.', and 'in vivo', 'in vitro', 'in situ', 'ex situ'.",
      category: "Style Points",
      execute(editor) {
        const matches = [];

        const iePattern = /\b(i\.e\.|e\.g\.)([^\s,])/gi;
        const ieMatches = findMatchesInSelector(editor, "body1, p, abstract", "tspIeEg", iePattern, "Add comma after i.e./e.g.", (m) => `${m[1]}, ${m[2]}`);
        matches.push(...ieMatches);

        const ltdPattern = /\bLtd\b(?!\.)/g;
        const ltdMatches = findMatchesInSelector(editor, "body1, p, abstract, aff, .affiliation-line", "tspLtd", ltdPattern, "Add period after Ltd", () => "Ltd.");
        matches.push(...ltdMatches);

        const pValuePattern = /\bp-value\b/gi;
        const pValueMatches = findMatchesInSelector(editor, "body1, p, abstract", "tspPValue", pValuePattern, "Italicize p in p-value", () => "<i>p</i>-value");
        matches.push(...pValueMatches);

        const hoursPattern = /(\d+)\s+(hours|hrs)\b/gi;
        const hoursMatches = findMatchesInSelector(editor, "body1, p, abstract", "tspTimeHours", hoursPattern, "Use 'h' for hours", (m) => `${m[1]} h`);
        matches.push(...hoursMatches);

        const minsPattern = /(\d+)\s+(minutes|mins)\b/gi;
        const minsMatches = findMatchesInSelector(editor, "body1, p, abstract", "tspTimeMins", minsPattern, "Use 'min' for minutes", (m) => `${m[1]} min`);
        matches.push(...minsMatches);

        const secsPattern = /(\d+)\s+(seconds|secs|sec)\b/gi;
        const secsMatches = findMatchesInSelector(editor, "body1, p, abstract", "tspTimeSecs", secsPattern, "Use 's' for seconds", (m) => `${m[1]} s`);
        matches.push(...secsMatches);

        const versusPattern = /\bversus\b/gi;
        const versusMatches = findMatchesInSelector(editor, "body1, p, abstract", "tspVersus", versusPattern, "Use italic 'vs.'", () => "<i>vs.</i>");
        matches.push(...versusMatches);

        const latinPattern = /\b(in\s+vivo|in\s+vitro|in\s+situ|ex\s+situ)\b/gi;
        const latinMatches = findMatchesInSelector(editor, "body1, p, abstract", "tspLatin", latinPattern, "Italicize foreign terms", (m) => `<i>${m[1]}</i>`);
        matches.push(...latinMatches);

        const dimPattern = /(\d+)\s*×\s*(\d+)\s*×\s*(\d+)\s*(mm|cm|m)\b/gi;
        const dimMatches = findMatchesInSelector(editor, "body1, p, abstract", "tspDimensions", dimPattern, "Repeat unit in dimensions", (m) => `${m[1]} ${m[4]} × ${m[2]} ${m[4]} × ${m[3]} ${m[4]}`);
        matches.push(...dimMatches);

        injectClientHighlightSpans(editor, matches, "highlight-style-points");
        return matches;
      }
    },
    {
      id: "tspBackMatter",
      name: "Back Matter Section Order",
      description: "Verifies that back matter sections (Acknowledgement, Funding Statement, Author Contributions, Data Availability, Ethics, Conflicts of Interest) follow the correct order.",
      doc: "### Back Matter Order\nBack matter sections must follow this exact order:\n1. Acknowledgement (optional)\n2. Funding Statement\n3. Author Contributions\n4. Availability of Data and Materials\n5. Ethics Approval\n6. Conflicts of Interest\n7. Supplementary Materials (optional)",
      category: "Back Matter",
      execute(editor) {
        const matches = [];
        const doc = editor.getDoc();
        
        const sections = Array.from(doc.querySelectorAll("h1, h2, h3, title1, sec-title"));
        const order = [
          "acknowledg",
          "funding",
          "contribution",
          "data",
          "ethic",
          "conflict",
          "supplementary"
        ];
        
        const found = [];
        sections.forEach((sec, idx) => {
          const text = (sec.textContent || "").toLowerCase();
          order.forEach((keyword, oIdx) => {
            if (text.includes(keyword)) {
              found.push({ heading: sec, orderIdx: oIdx, docIdx: idx });
            }
          });
        });

        for (let i = 0; i < found.length - 1; i++) {
          if (found[i].orderIdx > found[i+1].orderIdx) {
            const uid = `tspBackMatterOrder-${Math.random().toString(36).slice(2, 9)}`;
            matches.push({
              text: found[i].heading.textContent,
              suggested: found[i].heading.textContent + " (check back matter section order)",
              node: found[i].heading.firstChild || found[i].heading,
              start: 0,
              end: (found[i].heading.firstChild || found[i].heading).nodeValue?.length || found[i].heading.textContent.length,
              processed: false,
              name: "Back Matter Section Order Mismatch",
              styleId: "tspBackMatterOrder",
              uid
            });
          }
        }

        injectClientHighlightSpans(editor, matches, "highlight-backmatter");
        return matches;
      }
    },
    {
      id: "tspReferencesFormatting",
      name: "References Formatting",
      description: "Enforces lowercase 'doi:', removes ISBN/ISSN numbers, and ensures month abbreviations in reference lists.",
      doc: "### References Style Guidelines\n- DOI prefix must be lowercase ('doi:').\n- Delete ISBN and E-ISBN for books.\n- Delete ISSN for journals.\n- Abbreviate months in dates.",
      category: "References",
      execute(editor) {
        const matches = [];
        
        const doiPattern = /\bDOI\s*:\s*/g;
        const doiMatches = findMatchesInSelector(editor, "ref-list, .references, .ref-item", "tspRefDoi", doiPattern, "Use lowercase 'doi:'", () => "doi: ");
        matches.push(...doiMatches);

        const issnPattern = /\bISSN\s*[\d-]+\b/gi;
        const issnMatches = findMatchesInSelector(editor, "ref-list, .references, .ref-item", "tspRefIssn", issnPattern, "Remove ISSN number", () => "");
        matches.push(...issnMatches);

        const isbnPattern = /\bISBN\s*[\d-]+\b/gi;
        const isbnMatches = findMatchesInSelector(editor, "ref-list, .references, .ref-item", "tspRefIsbn", isbnPattern, "Remove ISBN number", () => "");
        matches.push(...isbnMatches);

        injectClientHighlightSpans(editor, matches, "highlight-references");
        return matches;
      }
    }
  ]
};
